import type { TowerType } from "@mmotdg/shared";
import type * as THREE from "three";
import { defaultTowerSkins } from "./default.js";
import type { TowerBuildOptions, TowerModelView, TowerSkin } from "./types.js";

export { applyTowerLevelVisual, TOWER_COLORS } from "./core.js";
export type { TowerBuildOptions, TowerModelView, TowerSkin };

const SKINS: Record<TowerType, Record<string, TowerSkin>> = {
  archer: { default: defaultTowerSkins.archer },
  cannon: { default: defaultTowerSkins.cannon },
  frost: { default: defaultTowerSkins.frost },
  lightning: { default: defaultTowerSkins.lightning },
  poison: { default: defaultTowerSkins.poison },
  support: { default: defaultTowerSkins.support },
  antiair: { default: defaultTowerSkins.antiair },
  economy: { default: defaultTowerSkins.economy },
};

const EQUIPPED_STORAGE_KEY = "mmotdg.equippedSkins";
const equipped: Partial<Record<TowerType, string>> = loadEquippedFromStorage();

function loadEquippedFromStorage(): Partial<Record<TowerType, string>> {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(EQUIPPED_STORAGE_KEY) : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function persistEquipped(): void {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(EQUIPPED_STORAGE_KEY, JSON.stringify(equipped));
  } catch {
    /* ignore quota / privacy mode */
  }
}

export function registerTowerSkin(skin: TowerSkin): void {
  if (!SKINS[skin.towerType]) SKINS[skin.towerType] = {};
  SKINS[skin.towerType][skin.id] = skin;
}

export function getTowerSkin(type: TowerType, skinId?: string): TowerSkin {
  const bucket = SKINS[type];
  const id = skinId ?? equipped[type] ?? "default";
  return bucket[id] ?? bucket.default;
}

export function listTowerSkins(type: TowerType): TowerSkin[] {
  return Object.values(SKINS[type] ?? {});
}

export function getEquippedSkinId(type: TowerType): string {
  return equipped[type] ?? "default";
}

export function equipTowerSkin(type: TowerType, skinId: string): void {
  if (skinId === "default") {
    delete equipped[type];
  } else {
    equipped[type] = skinId;
  }
  persistEquipped();
}

export function buildTowerModel(type: TowerType, options: TowerBuildOptions = {}): TowerModelView {
  const skin = getTowerSkin(type, options.skinId);
  return skin.build(options.level ?? 1, options.withSelectionRing ?? false);
}

export function disposeTowerModel(view: TowerModelView): void {
  view.body.geometry.dispose();
  (view.body.material as THREE.Material).dispose();
  view.selectionRing.geometry.dispose();
  (view.selectionRing.material as THREE.Material).dispose();
}
